"use client";

import { Box, Flex, FormControl, FormLabel, Icon, Skeleton, Tooltip } from "@chakra-ui/react";
import { HiOutlineArrowRightOnRectangle } from "react-icons/hi2";
import { useQueryClient } from "@tanstack/react-query";
import { signOut } from "next-auth/react";
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode, type UIEvent } from "react";
import { useForm } from "react-hook-form";
import { Button, Checkbox, Heading, Modal, ModalBody, ModalFooter, Text } from "@/components";
import { LabeledInput } from "@/components/shared/formElements/LabeledInput";
import { usePoliciesByUserIdGet } from "@/features/policies/hooks/usePoliciesByUserIdGet";
import { isChoreographerAgreementPolicy } from "@/features/policies/shared/policy-member-visibility";
import { useAccessToken, useTenantData } from "@/hooks";
import { useAuth } from "@/lib/contexts";
import { useThemeTokens } from "@/lib/theme/tokens";
import { PageTypeAdmin, QueryKeys, UserRoles } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { getNumericAuthUserId } from "@/lib/utils/teacher/resolve-teacher-profile-id";
import { WaiversPoliciesApi, type GetPolicyResponse } from "@/openapi/generated";

const WAIVER_CLEAR_PREFIX = "choreographer-waiver-clear:v1";

function waiverClearKey(tenantId: number, userId: number) {
  return `${WAIVER_CLEAR_PREFIX}:${tenantId}:${userId}`;
}

function readChoreographerWaiverClear(tenantId: number, userId: number) {
  if (typeof window === "undefined" || tenantId <= 0 || userId <= 0) return false;
  try {
    return window.localStorage.getItem(waiverClearKey(tenantId, userId)) === "1";
  } catch {
    return false;
  }
}

function writeChoreographerWaiverClear(tenantId: number, userId: number, clear: boolean) {
  if (typeof window === "undefined" || tenantId <= 0 || userId <= 0) return;
  try {
    const key = waiverClearKey(tenantId, userId);
    if (clear) window.localStorage.setItem(key, "1");
    else window.localStorage.removeItem(key);
  } catch {
    // Private mode can reject storage. The server response still decides.
  }
}

const blockingModalProps = {
  isOpen: true,
  onClose: () => undefined,
  closeOnOverlayClick: false,
  closeOnEsc: false,
  showCloseButton: false,
} as const;

function pendingChoreographerAgreements(policies: GetPolicyResponse[] | null | undefined) {
  return (policies ?? []).filter(
    (policy) =>
      isChoreographerAgreementPolicy(policy) &&
      policy.isRequired === true &&
      !policy.acceptedAt,
  );
}

export function ChoreographerAgreementGate({ children }: { children: ReactNode }) {
  const user = useAuth();
  const { tenantId } = useTenantData();
  const userId = getNumericAuthUserId(user) ?? 0;
  const isChoreographer = user?.activeRole === UserRoles.Choreographer;
  const policies = usePoliciesByUserIdGet({
    userId: isChoreographer ? userId : 0,
    pageSize: 1000,
  });
  const [knownClear, setKnownClear] = useState(() =>
    isChoreographer ? readChoreographerWaiverClear(tenantId, userId) : false,
  );

  useEffect(() => {
    setKnownClear(
      isChoreographer ? readChoreographerWaiverClear(tenantId, userId) : false,
    );
  }, [isChoreographer, tenantId, userId]);

  const pending = useMemo(
    () => pendingChoreographerAgreements(policies.data?.items),
    [policies.data],
  );

  useEffect(() => {
    if (!isChoreographer || userId <= 0 || tenantId <= 0 || !policies.isSuccess) return;
    const clear = pending.length === 0;
    writeChoreographerWaiverClear(tenantId, userId, clear);
    setKnownClear(clear);
  }, [isChoreographer, pending.length, policies.isSuccess, tenantId, userId]);

  if (!isChoreographer || userId <= 0) {
    return children;
  }

  if (policies.isPending && !knownClear) {
    return (
      <>
        {children}
        <AgreementModalSkeleton />
      </>
    );
  }

  if (policies.isError && !knownClear) {
    return (
      <>
        {children}
        <Modal {...blockingModalProps} title="Choreographer agreement" size={{ base: "full", md: "xl" }}>
          <ModalBody>
            <Text fontSize="sm">Couldn't load the agreement.</Text>
          </ModalBody>
          <ModalFooter>
            <Flex w="100%" justify="space-between" gap={3}>
              <LogOutButton />
              <Button onClick={() => void policies.refetch()}>Try again</Button>
            </Flex>
          </ModalFooter>
        </Modal>
      </>
    );
  }

  if (policies.isPending || policies.isError || pending.length === 0) {
    return children;
  }

  return (
    <>
      {children}
      <AgreementModal policies={pending} onSigned={() => void policies.refetch()} />
    </>
  );
}

function AgreementModalSkeleton() {
  const theme = useThemeTokens();

  return (
    <Modal {...blockingModalProps} title="Choreographer agreement" size={{ base: "full", md: "xl" }}>
      <ModalBody>
        <Flex direction="column" gap={4}>
          <Skeleton height="14px" width="92%" borderRadius="4px" />
          <Skeleton height="14px" width="68%" borderRadius="4px" />
          <Box
            h={{ base: "240px", md: "280px" }}
            p={{ base: 4, md: 5 }}
            borderRadius="8px"
            borderWidth="1px"
            borderColor={theme.borderGlass.default}
          >
            <Flex direction="column" gap={6}>
              {[0, 1, 2].map((section) => (
                <Flex key={section} direction="column" gap={2}>
                  <Skeleton height="14px" width="128px" borderRadius="4px" />
                  <Skeleton height="12px" width="100%" borderRadius="4px" />
                  <Skeleton height="12px" width="94%" borderRadius="4px" />
                  <Skeleton height="12px" width="78%" borderRadius="4px" />
                </Flex>
              ))}
            </Flex>
          </Box>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex direction="column" gap={6} w="100%">
          <Flex columnGap="10px" align="center">
            <Skeleton boxSize="18px" borderRadius="4px" flexShrink={0} />
            <Skeleton height="14px" flex="1" borderRadius="4px" />
          </Flex>
          <Flex direction="column" gap={2}>
            <Skeleton height="12px" width="188px" borderRadius="4px" />
            <Skeleton height="40px" width="100%" borderRadius="8px" />
          </Flex>
          <Flex justify="space-between" gap={3}>
            <Skeleton height="40px" width="112px" borderRadius="8px" />
            <Skeleton height="40px" width="112px" borderRadius="8px" />
          </Flex>
        </Flex>
      </ModalFooter>
    </Modal>
  );
}

function logOut() {
  return signOut({ callbackUrl: PageTypeAdmin.SignIn });
}

function LogOutButton({ isDisabled = false }: { isDisabled?: boolean }) {
  const theme = useThemeTokens();

  return (
    <Button
      variant="ghost"
      leftIcon={<Icon as={HiOutlineArrowRightOnRectangle} boxSize="18px" />}
      onClick={() => void logOut()}
      isDisabled={isDisabled}
      sx={{
        "@media (hover: hover)": {
          "&:hover:not(:disabled):not([disabled]):not([aria-disabled=true])": {
            bg: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(11,13,16,0.08)",
            color: "red.400",
          },
        },
      }}
    >
      Log out
    </Button>
  );
}

function agreementSections(text: string) {
  return text
    .trim()
    .split(/\n\s*\n/)
    .filter(Boolean)
    .map((block) => {
      const lines = block.split("\n");
      const heading = lines[0]?.trim() ?? "";
      const body = lines.slice(1).join(" ").trim();
      const titled = body.length > 0 && heading.length > 0 && heading.length <= 40;
      return titled ? { heading, body } : { heading: "", body: block.trim() };
    });
}

function AgreementModal({
  policies,
  onSigned,
}: {
  policies: GetPolicyResponse[];
  onSigned: () => void;
}) {
  const theme = useThemeTokens();
  const user = useAuth();
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const queryClient = useQueryClient();
  const waiverScrollRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register, watch } = useForm<{ signedName: string }>({
    defaultValues: {
      signedName: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(),
    },
  });
  const signedName = watch("signedName") ?? "";
  const title = policies.length === 1
    ? policies[0]?.name || "Choreographer agreement"
    : "Choreographer agreement";

  const unlockIfScrolledToEnd = useCallback((container: HTMLElement) => {
    if (container.scrollHeight - container.scrollTop - container.clientHeight <= 24) {
      setHasScrolledToBottom(true);
    }
  }, []);

  useEffect(() => {
    const container = waiverScrollRef.current;
    if (!container) return;
    const initialTimer = window.setTimeout(() => unlockIfScrolledToEnd(container), 100);
    return () => window.clearTimeout(initialTimer);
  }, [policies, unlockIfScrolledToEnd]);

  const handleWaiverScroll = (event: UIEvent<HTMLDivElement>) => {
    unlockIfScrolledToEnd(event.currentTarget);
  };

  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (hasScrolledToBottom) {
        setChecked(event.target.checked);
      }
    },
    [hasScrolledToBottom],
  );

  const sign = async () => {
    const name = signedName.trim();
    if (!accessToken || !user?.email || !checked || !hasScrolledToBottom || name.length < 2) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const api = createAuthenticatedApi(WaiversPoliciesApi, accessToken);
      for (const policy of policies) {
        await api.apiPoliciesIdAcceptPost({
          id: Number(policy.policyId),
          xTenantID: tenantId,
          acceptPolicyRequest: {
            userEmail: user.email,
            policyName: policy.name ?? "Choreographer agreement",
            policyDisplayText: policy.displayText ?? null,
            signedName: name,
          },
        });
      }
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Dances] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Choreographers] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Waivers] });
      onSigned();
    } catch (signError) {
      setError(signError instanceof Error ? signError.message : "Could not save the signature.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal {...blockingModalProps} title={title} size={{ base: "full", md: "xl" }}>
      <ModalBody>
        <Text fontSize="sm" color={theme.text.secondary} lineHeight="1.6" mb={4}>
          Please read the agreement carefully. Scroll to the end before you can accept it.
        </Text>
        <Box
          ref={waiverScrollRef}
          h={{ base: "240px", md: "280px" }}
          overflowY="scroll"
          overscrollBehavior="contain"
          onScroll={handleWaiverScroll}
          p={{ base: 4, md: 5 }}
          borderRadius="8px"
          borderWidth="1px"
          borderColor={theme.borderGlass.default}
          css={{
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: theme.borderGlass.default,
              borderRadius: "4px",
            },
          }}
        >
          <Flex direction="column" gap={6}>
            {policies.map((policy) => (
              <Flex key={String(policy.policyId)} direction="column" gap={6}>
                {policies.length > 1 ? (
                  <Heading size="md" color={theme.text.primary}>
                    {policy.name || "Choreographer agreement"}
                  </Heading>
                ) : null}
                {agreementSections(policy.displayText || policy.name || "").map((section) => (
                  <Box key={`${policy.policyId}-${section.heading || section.body}`}>
                    {section.heading ? (
                      <Text fontSize="sm" fontWeight="600" color={theme.text.primary} mb={2}>
                        {section.heading}
                      </Text>
                    ) : null}
                    <Text fontSize="sm" color={theme.text.secondary} lineHeight="1.75">
                      {section.body}
                    </Text>
                  </Box>
                ))}
              </Flex>
            ))}
          </Flex>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Flex direction="column" gap={6} w="100%">
          <Tooltip
            label="Scroll to the end of the agreement before you can accept it."
            isDisabled={hasScrolledToBottom}
            hasArrow
            placement="top"
            openDelay={200}
          >
            <Box as="span" display="inline-block" w="100%">
              <FormControl>
                <Flex columnGap="10px" align="flex-start">
                  <Checkbox
                    mt="1px"
                    id="choreographer-agreement"
                    isChecked={checked}
                    onChange={handleCheckboxChange}
                    isDisabled={!hasScrolledToBottom}
                  />
                  <FormLabel
                    htmlFor="choreographer-agreement"
                    mb="0"
                    cursor={hasScrolledToBottom ? "pointer" : "not-allowed"}
                    fontSize="sm"
                  >
                    <Text variant="primary" fontSize="sm" color={theme.text.primary} lineHeight="1.5">
                      I have read and agree to the terms of this season's choreographer agreement.
                    </Text>
                  </FormLabel>
                </Flex>
              </FormControl>
            </Box>
          </Tooltip>

          <LabeledInput
            name="signedName"
            label="Type your full name to sign"
            register={register}
            required
            placeholder="Full name"
            mb={0}
          />

          {error ? (
            <Text color={theme.accent.primary} fontSize="sm">
              {error}
            </Text>
          ) : null}

          <Flex justify="space-between" gap={3} flexWrap="wrap">
            <LogOutButton isDisabled={saving} />
            <Button
              isDisabled={!hasScrolledToBottom || !checked || signedName.trim().length < 2}
              isLoading={saving}
              onClick={() => void sign()}
            >
              Continue
            </Button>
          </Flex>
        </Flex>
      </ModalFooter>
    </Modal>
  );
}
